<template>
  <footer class="dock" :class="dockClass">
    <button v-for="item in menuItems" :key="item.name" :class="['dock-plain', item.center ? 'dock-center' : '', { 'dock-plain--active': isActive(item.path) }]"
      :aria-label="item.label" @click="handleClick(item)">
      <span v-if="item.center" class="glow"></span>
      <component :is="isActive(item.path) ? item.activeIcon : item.icon"
        :class="[{ active: isActive(item.path) }, 'icon']" />
      <span v-if="activeLabel[item.name]" class="notification-dot"></span>
    </button>
  </footer>
</template>

<script setup lang="ts">
import HeartIco from "@/assets/images/rec-img/Heart.svg";
import activeHeartIco from "@/assets/images/rec-img/Heart-active.svg";
import HumanIco from "@/assets/images/rec-img/Human.svg";
import CardsIco from "@/assets/images/rec-img/Cards.svg";
import MailIco from "@/assets/images/rec-img/Mail.svg";
import activeMailIco from "@/assets/images/rec-img/activeMail.svg";
import activeProfileIco from "@/assets/images/rec-img/profile-active.svg";
import ReelsIco from "@/assets/images/rec-img/reels.svg";
import activeReelsIco from "@/assets/images/rec-img/reels-active.svg";
import { useRoute, useRouter } from "vue-router";
import { computed, shallowRef } from "vue";
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
const router = useRouter();
const route = useRoute();

const props = defineProps({
  color: String,
  activeItem: String,
  isActiveLabel: {
    type: Object as () => Record<string, boolean>,
    default: () => ({
      messenger: false,
      likes: false,
      profile: false,
      cards: false,
      reels: false
    }),
  },
})

const activeLabel = computed(() => props.isActiveLabel || {});

const menuItems = shallowRef([
  { name: 'likes', label: 'Favorites', path: { name: "Likes" }, icon: HeartIco, activeIcon: activeHeartIco, center: false },

  { name: 'profile', label: 'Profile', path: { name: "Profile" }, icon: HumanIco, activeIcon: activeProfileIco, center: false },
  { name: 'cards', label: 'Cards', path: '/foundation/search', icon: CardsIco, activeIcon: CardsIco, center: true },
  { name: 'messenger', label: 'Messages', path: '/under-nav/messenger', icon: MailIco, activeIcon: activeMailIco, center: false },
  { name: 'reels', label: 'reels', path: '/reels', icon: ReelsIco, activeIcon: activeReelsIco, center: false, }
])

const dockClass = computed(() => {
  return props.color === 'white' ? 'dock-white' : 'dock-default'
})


const isActive = (pathOrName: string | { name: string }) => {
  const currentName = route.name;
  const currentPath = route.path;

  const targetName = typeof pathOrName === 'object' ? pathOrName.name : null;
  const targetPath = typeof pathOrName === 'string' ? pathOrName : null;

  if (props.activeItem && props.activeItem === (targetName ?? targetPath)) {
    return true;
  }

  if (targetName === "Likes" && currentName === "TopProfiles") {
    return true;
  }

  return (
    (targetName && currentName === targetName) ||
    (targetPath && currentPath === targetPath)
  );
};

const notify = () => {
  toast.warning("Раздел в разработке");
};

const handleClick = (item: any) => {
  if (item.customAction === 'notify') {
    notify();
  }
  if (!item.path) return;
  if (isActive(item.path)) return;

  try {
    router.push(item.path);
  } catch (err) {
    console.error('Navigation error:', err);
  }

}
</script>

<style scoped>
.dock {
  --dock-bottom: max(10px, env(safe-area-inset-bottom, 0px));
  --dock-side: clamp(14px, 4vw, 22px);
  height: var(--dock-visual-h, 72px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(10px, 3vw, 18px);
  padding: 9px clamp(18px, 5vw, 28px);
  z-index: 998;
  width: calc(100% - (var(--dock-side) * 2));
  max-width: 520px;
  margin-top: auto;
  position: fixed;
  bottom: var(--dock-bottom);
  left: 50%;
  right: auto;

  background: var(--dock-bg);
  background: color-mix(in srgb, var(--dock-bg) 78%, transparent);
  border: 1px solid var(--dock-border);
  border-color: color-mix(in srgb, var(--dock-border) 74%, transparent);
  border-radius: 28px;
  box-shadow:
    0 18px 44px rgba(15, 17, 21, 0.16),
    0 1px 0 rgba(255, 255, 255, 0.36) inset;
  backdrop-filter: blur(22px) saturate(1.35);
  -webkit-backdrop-filter: blur(22px) saturate(1.35);

  transform: translate3d(-50%, 0, 0);
  will-change: transform;
}

/* legacy prop support */
.dock.dock-white {
  background: var(--dock-bg);
  background: color-mix(in srgb, var(--dock-bg) 78%, transparent);
}

.notification-dot {
  position: absolute;
  top: 4px;
  right: 5px;
  width: 8px;
  height: 8px;
  background: #ff3b30;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(255, 59, 48, 0.6);
  animation: pulse 1.8s infinite ease-in-out;
}

.dock-plain {
  --hit: clamp(38px, 10vw, 46px);
  width: var(--hit);
  height: var(--hit);
  display: grid;
  place-items: center;
  background: transparent;
  border: 0;
  box-shadow: none;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  transition: transform 200ms cubic-bezier(.2, .8, .25, 1),
    filter 220ms ease,
    opacity 220ms ease;
  opacity: .9;
  position: relative;
  color: var(--dock-icon);
}

.dock-plain::before {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: inherit;
  background: rgba(255, 21, 105, 0.12);
  background: color-mix(in srgb, var(--dock-icon-active) 12%, transparent);
  opacity: 0;
  transform: scale(0.7);
  transition: opacity 220ms ease, transform 220ms cubic-bezier(.2, .8, .25, 1);
}

.dock-plain .icon {
  width: clamp(22px, 4.8vw, 28px);
  height: clamp(22px, 4.8vw, 28px);
  display: block;
  filter: drop-shadow(0 0 0 transparent);
  position: relative;
  z-index: 1;
  transition: transform 220ms cubic-bezier(.2, .8, .25, 1), opacity 220ms ease;
}

.dock-plain:not(.dock-plain--active):not(.dock-center) .icon {
  filter: grayscale(1) saturate(0) brightness(0.78) !important;
  opacity: 0.92;
}

.dock-plain .icon.active {
  color: var(--dock-icon-active);
  transform: translateY(-1px) scale(1.06);
}

.dock-plain--active {
  opacity: 1;
}

.dock-plain--active::before {
  opacity: 1;
  transform: scale(1);
}

.dock-plain:hover {
  transform: translateY(-2px) scale(1.06);
  filter: brightness(1.1);
  opacity: 1;
}

.dock-plain:active {
  transform: translateY(0) scale(.94);
}

/* CENTER BUTTON — НЕ ТРОГАЕМ ВИЗУАЛ */
.dock-center {
  --size: clamp(52px, 14vw, 62px);
  position: relative;
  width: var(--size);
  height: var(--size);
  border-radius: 999px;
  border: 0;
  display: grid;
  place-items: center;
  cursor: pointer;
  background: radial-gradient(120% 120% at 30% 20%, #ff7ac3 0%, #ff3ea3 60%, #ee2e93 100%);
  box-shadow: 0 12px 26px rgba(239, 49, 146, .38),
    0 0 0 1px rgba(255, 255, 255, .08) inset;
  color: #fff;
  isolation: isolate;
  transition: transform 220ms cubic-bezier(.2, .9, .25, 1),
    filter 220ms ease;
}

.dock-center .glow {
  position: absolute;
  inset: -14px;
  border-radius: inherit;
  z-index: -1;
  background: radial-gradient(70% 70% at 50% 50%,
      rgba(247, 94, 164, .45),
      rgba(247, 94, 164, 0) 65%);
  pointer-events: none;
}

.dock-center .icon {
  width: clamp(22px, 5vw, 30px);
  height: clamp(22px, 5vw, 30px);
  filter: drop-shadow(0 2px 10px rgba(0, 0, 0, .35));
  fill: #FFFFFF;
  stroke: #EE2E93FF;
}

.dock-center:hover {
  transform: translateY(-2px) scale(1.06);
  filter: brightness(1.05);
}

.dock-center:active {
  transform: translateY(0) scale(.94);
}

/* MOBILE */
@media (max-width: 640px) {
  .dock {
    --dock-bottom: max(8px, env(safe-area-inset-bottom, 0px));
    height: var(--dock-visual-h, 68px);
    padding: 8px clamp(16px, 6vw, 26px);
    background: var(--dock-bg);
    background: color-mix(in srgb, var(--dock-bg) 76%, transparent);
    z-index: 70;
  }
}

/* ACCESSIBILITY */
@media (prefers-reduced-motion: reduce) {

  .dock-plain,
  .dock-center {
    transition: none !important;
    animation: none !important;
  }
}

@keyframes pulse {

  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }

  50% {
    transform: scale(1.6);
    opacity: 0.6;
  }
}
</style>
